import { Box, Button, Container, Divider, Skeleton, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Agency.css";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { agencyApi } from "../../API/Api";
import { buttonSx } from "../../../store/presetsNew";
import { tokens } from "../../../store/tokens";

const settings = {
  infinite: true,
  arrows: true,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  draggable: true,
  dots: false,
  autoplay: true,
  autoplaySpeed: 4000,
  responsive: [
    { breakpoint: 1200, settings: { slidesToShow: 3 } },
    { breakpoint: 900, settings: { slidesToShow: 2 } },
    { breakpoint: 600, settings: { slidesToShow: 1, initialSlide: 1 } },
  ],
};

const sectionTitleSx = {
  fontFamily: tokens.font.heading,
  fontWeight: tokens.type.h2.fontWeight,
  fontSize: { xs: "1.6rem", md: tokens.type.h2.fontSize },
  color: tokens.textOn.light.primary,
};
const sectionSubtitleSx = {
  fontFamily: tokens.font.body,
  fontWeight: 500,
  fontSize: tokens.type.body1.fontSize,
  color: tokens.textOn.light.secondary,
};
const primaryButtonSx = {
  borderRadius: `${tokens.button.radius}px`,
  height: tokens.button.height.md,
  px: `${tokens.button.paddingX.md}px`,
  fontFamily: tokens.font.body,
  fontWeight: tokens.type.button.fontWeight,
  fontSize: tokens.button.fontSize.md,
  textTransform: tokens.type.button.textTransform,
  bgcolor: tokens.bg.brand,
  color: tokens.color.white,
  boxShadow: "none",
  transition: "background-color 0.2s ease",
  "&:hover": { bgcolor: tokens.bg.brandDark },
  width: { xs: "100%", sm: "auto" },
};

const Agency = () => {
  const navigate = useNavigate();
  const [agencyData, setAgencyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(agencyApi)
      .then((res) => res.json())
      .then((result) => {
        setAgencyData(Array.isArray(result) ? result.slice(0, 12) : []);
        setLoading(false);
      })
      .catch((error) => {
        console.log("agencyData Error => ", error); // was previously unhandled
        setLoading(false);
      });
  }, []);

  return (
    <Box sx={{ backgroundColor: tokens.bg.page, py: { xs: 5, md: 6 } }}>
      <Container>
        <Stack
          component={motion.div}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5 }}
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography component="h2" sx={sectionTitleSx}>
              Agencies
            </Typography>
            <Typography sx={sectionSubtitleSx}>
              Established teams ready to run your project end to end
            </Typography>
          </Box>

          <Button
            component={motion.button}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            variant="contained"
            onClick={() => navigate("/agentlist")}
            sx={primaryButtonSx}
          >
            More agencies
          </Button>
        </Stack>

        <Divider sx={{ mb: 3, borderColor: tokens.color.line }} />

        {loading ? (
          <Slider {...settings}>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ px: 1, pb: 4 }}>
                <Skeleton variant="rounded" height={260} sx={{ borderRadius: `${tokens.radius.lg}px` }} />
              </Box>
            ))}
          </Slider>
        ) : (
          (agencyData[0]?.c_refer || agencyData[1]?.c_refer) && (
            <Slider {...settings}>
              {agencyData.map((element) => {
                const name = element.cname ?? "";
                const slogan = element.cslogan ?? "";
                return (
                  <motion.div
                    key={element.c_refer}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Box sx={{ px: 1, pb: 4 }}>
                      <Box
                        component={motion.div}
                        whileHover={{ y: -4 }}
                        sx={{
                          position: "relative",
                          borderRadius: `${tokens.radius.lg}px`,
                          overflow: "hidden",
                          boxShadow: tokens.shadow.md,
                        }}
                        className="agency"
                      >
                        <Box
                          component="img"
                          src={element.logo}
                          alt={name}
                          sx={{
                            width: "100%",
                            height: 180,
                            objectFit: "cover",
                            filter: "brightness(0.8)",
                            transition: "filter 0.3s ease",
                          }}
                        />

                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            width: "100%",
                            height: "45%",
                            background: `linear-gradient(to top, ${tokens.color.overlay}, rgba(0,0,0,0))`,
                          }}
                        />

                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            width: "100%",
                            px: 2,
                            py: 1.5,
                            color: tokens.color.white,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: tokens.font.heading,
                              fontWeight: 700,
                              fontSize: tokens.type.h5.fontSize,
                              textShadow: "1px 1px 3px rgba(0,0,0,0.8)",
                            }}
                          >
                            {name.length > 18 ? name.slice(0, 18) + "…" : name}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.5,
                              fontFamily: tokens.font.body,
                              color: "rgba(255,255,255,0.85)",
                              fontSize: tokens.type.body2.fontSize,
                              textShadow: "1px 1px 2px rgba(0,0,0,0.7)",
                            }}
                          >
                            {slogan.length > 28 ? slogan.slice(0, 28) + "…" : slogan}
                          </Typography>

                          <Button
                            component={motion.button}
                            whileHover={{ y: -2 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => navigate("/profile/" + element.c_refer)}
                            sx={{ ...buttonSx("sm", "solid"), mt: 1 }}
                          >
                            View profile
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </Slider>
          )
        )}
      </Container>
    </Box>
  );
};

export default Agency;